import { Edge } from '@xyflow/react'
import { TreeFragment } from 'generated/resources/graphql'

import {
  CustomNode,
  SIZES,
  SerializedTreeNode,
} from 'modules/job_catalog/components/DevJobCatalogTree/types'

interface ReturnType {
  nodes: CustomNode[]
  edges: Edge[]
}

interface LayoutResult {
  width: number
  x: number
  y: number
  nodes: CustomNode[]
  edges: Edge[]
}

const useTreeDataToNodes = (treeData: TreeFragment): ReturnType => {
  if (!treeData?.serializedTree) {
    return { nodes: [], edges: [] }
  }

  const parsedTree: SerializedTreeNode = JSON.parse(treeData.serializedTree)

  const processNode = (
    node: SerializedTreeNode,
    level: number,
    startX: number,
    parentId?: string
  ): LayoutResult => {
    const nodeId = node.node_uuid || String(node.id)
    const children = node.children || []

    if (children.length === 0) {
      const x = startX
      const y = level * SIZES.VERTICAL_SPACING

      return {
        width: SIZES.NODE_WIDTH,
        x,
        y,
        nodes: [
          {
            id: nodeId,
            type: 'custom-node',
            position: { x, y },
            data: {
              label: node.name,
              name: node.name,
              type: node.type,
              description: node.description ?? '',
              id: node.id,
              node_uuid: node.node_uuid,
              expanded: false,
              childrenCount: 0,
            },
          },
        ],
        edges: parentId
          ? [
              {
                id: `${parentId}-${nodeId}`,
                type: 'custom-edge',
                source: parentId,
                target: nodeId,
              },
            ]
          : [],
      }
    }

    let currentX = startX
    const childrenResults = children.map((child) => {
      const result = processNode(child, level + 1, currentX, nodeId)
      currentX = result.x + result.width + SIZES.HORIZONTAL_SPACING
      return result
    })

    const totalWidth =
      childrenResults.reduce((sum, child) => sum + child.width, 0) +
      (childrenResults.length - 1) * SIZES.HORIZONTAL_SPACING
    const x = startX + (totalWidth - SIZES.NODE_WIDTH) / 2
    const y = level * SIZES.VERTICAL_SPACING

    const allNodes = [
      {
        id: nodeId,
        type: 'custom-node',
        position: { x, y },
        data: {
          label: node.name,
          name: node.name,
          type: node.type,
          description: node.description ?? '',
          id: node.id,
          node_uuid: node.node_uuid,
          expanded: node.type === 'jobcatalog_treeroot',
          expandable: true,
          childrenCount: children.length,
        },
      },
      ...childrenResults.flatMap((result) => result.nodes),
    ]

    const allEdges = [
      ...(parentId
        ? [
            {
              id: `${parentId}-${nodeId}`,
              type: 'custom-edge',
              source: parentId,
              target: nodeId,
            },
          ]
        : []),
      ...childrenResults.flatMap((result) => result.edges),
    ]

    return {
      width: Math.max(SIZES.NODE_WIDTH, totalWidth),
      x,
      y,
      nodes: allNodes,
      edges: allEdges,
    }
  }

  const result = processNode(parsedTree, 0, 0)
  return { nodes: result.nodes, edges: result.edges }
}

export default useTreeDataToNodes
